import type {ReactElement} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {EventWizardOptionTileProps} from './event-wizard-option-tile.types';
import styles from './event-wizard-option-tile.module.scss';

/**
 * Generic, presentational selectable tile for the event wizard (no Redux, no feature knowledge).
 * Renders an icon circle, a title, and a description inside a ClickableDiv card with selected
 * (blue-border) and disabled visual states. Reused for every registration-mode tile.
 */
export function EventWizardOptionTile({icon, title, description, isSelected, onClick, isDisabled = false}: EventWizardOptionTileProps): ReactElement {
  const getCardClasses = (): string => {
    const classes = [styles.tile, 'flex-column-align-start', 'corner-radius-8', 'spacing-p-3', 'gap-4', '_full-width'];

    if (isSelected) {
      classes.push(styles.selected);
    }

    if (isDisabled) {
      classes.push(styles.disabled);
    }

    return classes.join(' ');
  };

  const handleClick = (): void => {
    if (isDisabled) {
      return;
    }
    onClick();
  };

  // ClickableDiv is a role="button" div that is always focusable, so a disabled tile would otherwise take
  // keyboard focus and announce as actionable while swallowing the press. Its own props spread after those
  // defaults, so both attributes override.
  return (
    <ClickableDiv className={getCardClasses()} onClick={handleClick} aria-disabled={isDisabled} tabIndex={isDisabled ? -1 : 0}>
      <div className={`${styles.iconCircle} flex-center corner-radius-full flex-shrink-0`}>
        <Icon icon={icon} size={IconSize.SIZE_ICON_20} isDisabled={isDisabled} className={isSelected ? 'ct-brand-primary' : undefined} />
      </div>
      <div className="flex-column-align-start">
        <span className={`${styles.title} heading-h8 ct-neutral-primary`}>{title}</span>
        <Text className="ct-neutral-secondary" val={description} size={TextSize.SM} />
      </div>
    </ClickableDiv>
  );
}
